<template>
  <div
    :id="useSlugify(slice.primary.title || '')"
    :class="`wrapper_${index}`"
    class="my-10 overflow-hidden md:my-20"
  >
    <div
      class="mx-auto grid grid-cols-1 lg:grid-cols-2"
      :class="useTheme(slice.primary.theme || 'Default')"
    >
      <!-- Text -->
      <div class="h-full p-10">
        <h2 class="block text-header2 md:text-desktop-header2">
          {{ slice.primary.title }}
        </h2>
        <PrismicRichText
          :field="slice.primary.description"
          class="mt-10 text-body-copy text-charcoal-100"
        />

        <!-- Table -->
        <div
          v-if="slice.variation === 'table'"
          class="mt-20"
        >
          <table class="my-[40px] w-full divide-y divide-charcoal-100">
            <tr
              v-for="(row, index) in slice.primary.rows"
              :key="index"
              class="flex items-start justify-between py-2"
            >
              <td>{{ row.column_1 }}</td>
              <td>{{ row.column_2 }}</td>
            </tr>
          </table>
        </div>

        <!-- Accordion -->
        <div
          v-if="slice.variation === 'accordion'"
          class="my-[40px] w-full divide-y divide-charcoal-100 text-center"
        >
          <dl class="mt-10 space-y-4 divide-y divide-charcoal-100 text-left ">
            <Disclosure
              v-for="(section, index) in slice.primary.tabs"
              :key="index"
              ref="tabs"
              v-slot="{ open, close }"
              as="div"
              class="pt-6"
              :default-open="index == 0 && slice.primary.open_first"
            >
              <dt>
                <DisclosureButton
                  :ref="_ => (tabs[index] = close)"
                  class="flex w-full items-start justify-between text-left"
                  @click="toggleTabs(index)"
                >
                  <p class="text-body-copy md:text-desktop-body-copy">
                    {{ section.title }}
                  </p>

                  <span class="ml-6 flex h-7 items-center">
                    <PlusIcon
                      v-if="!open"
                      class="size-6"
                      aria-hidden="true"
                    />
                    <MinusIcon
                      v-else
                      class="size-6"
                      aria-hidden="true"
                    />
                  </span>
                </DisclosureButton>
              </dt>

              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-out"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <DisclosurePanel
                  as="dd"
                  class="mt-2 pr-12"
                >
                  <PrismicRichText
                    :field="section.content"
                    class="text-body-copy text-charcoal-100"
                  />
                  <UIButton
                    v-if="prismic.asLink(section.link)"
                    :type="'text'"
                    :label="'Find out more'"
                    :link="prismic.asLink(section.link)"
                    class="my-5 block"
                  />
                </DisclosurePanel>
              </transition>
            </Disclosure>
          </dl>
        </div>

        <!-- Link -->
        <div
          v-if="slice.primary.link"
          class="mt-10"
        >
          <UIButton
            v-if="prismic.asLink(slice.primary.link)"
            :type="'text'"
            :label="'Find out more'"
            :link="prismic.asLink(slice.primary.link)"
            class="my-5 block text-large-copy"
          />
        </div>
      </div>

      <!-- Image -->
      <div class="max-w-none md:h-full">
        <div
          v-if="slice.variation === 'default' || slice.variation === 'table'"
          class="swiper md:h-full"
        >
          <div class="swiper-wrapper">
            <div
              v-for="(image, index) in slice.primary.gallery"
              :key="index"
              class="swiper-slide"
              :class="`slide--${index}`"
            >
              <PrismicImage
                :field="image.image"
                class="object-cover md:h-full"
              />
            </div>
          </div>

          <!-- If navigation buttons are needed -->
          <div class="swiper-pagination" />
          <ArrowLeft class="swiper-button-prev !w-20" />
          <ArrowRight class="swiper-button-next !w-20" />
        </div>

        <div
          v-if="slice.variation === 'accordion'"
          class="h-full"
        >
          <PrismicImage
            :field="slice.primary.tabs[current]?.image"
            class="object-cover md:h-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Swiper from "swiper";
import { Navigation, Pagination, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import type { Content } from "@prismicio/client";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { MinusIcon, PlusIcon } from "@heroicons/vue/24/outline";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ArrowLeft from "~/assets/svgs/arrow_left.svg";
import ArrowRight from "~/assets/svgs/arrow_right.svg";

gsap.registerPlugin(ScrollTrigger);

const prismic = usePrismic();

// The array passed to `getSliceComponentProps` is purely optional.
// Consider it as a visual hint for you when templating your slice.
const props = defineProps(
  getSliceComponentProps<Content.TabbedContentSlice>([
    "slice",
    "index",
    "slices",
    "context",
  ]),
);

const tabs = ref([]);
const current = ref<number>(0);

const toggleTabs = (index: number) => {
  current.value = index;
  tabs.value.filter((_: unknown, key: number) => key !== index).forEach((close: () => unknown) => close());
};

onMounted(() => {
  if (props.index != 0) {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: `.wrapper_${props.index.toString()}`,
        start: "top 80%",
        end: "bottom bottom",
        scrub: true,
      },
    });

    tl.from(`.wrapper_${props.index.toString()}`, { y: "50px", opacity: 0, duration: 0.5 });
  }

  // init Swiper:

  const swiperCarousel = new Swiper(".swiper", {
    modules: [Navigation, Pagination, EffectFade],
    direction: "horizontal",
    speed: 1000,

    effect: "fade",

    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },

    pagination: {
      el: ".swiper-pagination",
      type: "bullets",
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 5,
    },
  });
});
</script>
